import { useMemo } from "react";

import { replaceStringPlaceholder } from "@ui/utils/replaceStringPlaceholder/replaceStringPlaceholder";
import classNames from "classnames";
import DomPurify from "isomorphic-dompurify";

import { LongArrowForwardIcon } from "@jti/brand-icons/long-arrow-forward";
import type { AccordionItemIdentifier } from "@jti/core-components/components/Accordion";
import { Accordion, AccordionItem } from "@jti/core-components/components/Accordion";
import { Link } from "@jti/core-components/components/Link";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { FaqJsonLD } from "./FaqJsonLD";

import styles from "./Faq.module.scss";

const { sanitize } = DomPurify;

export type FaqItem = {
  faqTitle: string;
  faqDetails: string;
};

export type FaqProps = {
  title: string;
  details: string;
  detailsLink: string;
  showAdditionalHelp?: boolean;
  additionalHelpText?: string;
  additionalHelpEmail?: string;
  faqList: FaqItem[];
};

export const Faq: React.FC<FaqProps> = ({
  title,
  details,
  detailsLink,
  showAdditionalHelp = true,
  additionalHelpText,
  additionalHelpEmail,
  faqList = [],
}) => {
  const additionalString = useMemo(
    () =>
      replaceStringPlaceholder(additionalHelpText ?? "", [
        {
          placeholder: "helpEmail",
          value: additionalHelpEmail ?? "",
          tag: "a",
          additionalProps: `href="mailto:${additionalHelpEmail ?? ""}"`,
        },
      ]),
    [additionalHelpText, additionalHelpEmail]
  );

  const dispatchGTMEvent = (faqName: string) => {
    dispatchTrackingEvent(TrackingEvents.TrackingFaqLink, { faq_name: faqName });
  };

  const handleAccordionToggle = ({ id }: AccordionItemIdentifier, isExpanded: boolean) => {
    if (isExpanded) {
      dispatchGTMEvent(id);
    }
  };

  if (!faqList.length) {
    return null;
  }

  return (
    <>
      <FaqJsonLD faqList={faqList} />
      <div className={classNames(styles.faqWrapper)}>
        <div className={styles.leftBlock}>
          <h2 className={styles.title} data-testid="faq-title-typography">
            {title}
          </h2>
          <Link
            href={detailsLink}
            size="md"
            iconAfter={LongArrowForwardIcon}
            isMuted
            isStandalone
            onClick={() => dispatchGTMEvent(title)}
            data-testid="faq-more-link"
          >
            {details}
          </Link>
        </div>
        <div className={styles.rightBlock}>
          <Accordion onToggle={handleAccordionToggle}>
            {faqList.map(({ faqTitle, faqDetails }) => (
              <AccordionItem key={faqTitle} id={faqTitle} title={faqTitle}>
                <p className={styles.faqItemContent}>{faqDetails}</p>
              </AccordionItem>
            ))}
          </Accordion>
          {showAdditionalHelp && additionalString && (
            <p
              className={styles.help}
              dangerouslySetInnerHTML={{ __html: sanitize(additionalString) }}
              data-testid={"faq-help"}
            />
          )}
        </div>
      </div>
    </>
  );
};
